import { useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useSubmitContactQuery } from '@/hooks/use-contact-mutations';

const MAX_MESSAGE_LENGTH = 4000;

export function ContactForm() {
  const { user } = useAuth();
  const { mutate, isPending, isSuccess } = useSubmitContactQuery();

  const defaultName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : '';
  const defaultEmail = user?.email ?? '';

  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutate({
      name,
      email: email || undefined,
      phone: phone || undefined,
      message,
    });
  }

  if (isSuccess) {
    return (
      <div className="rounded-lg border bg-card p-8 text-center">
        <h2 className="text-xl font-semibold mb-2">Message sent!</h2>
        <p className="text-muted-foreground">
          Thank you for reaching out. We will get back to you as soon as possible.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="contact-name">
          Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="contact-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your full name"
          required
          maxLength={100}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-email">Email</Label>
        <Input
          id="contact-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-phone">Phone</Label>
        <Input
          id="contact-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+91 98765 43210"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-message">
          Message <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="contact-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="How can we help you?"
          required
          maxLength={MAX_MESSAGE_LENGTH}
          rows={6}
          className="resize-none"
        />
        <p className="text-xs text-muted-foreground text-right">
          {message.length} / {MAX_MESSAGE_LENGTH}
        </p>
      </div>

      <Button
        type="submit"
        disabled={isPending || !name.trim() || !message.trim()}
        className="w-full"
      >
        {isPending ? 'Sending…' : 'Send Message'}
      </Button>
    </form>
  );
}
